import { useState } from "react";
import { useTranslation } from "react-i18next";

import { guessDeviceName, passkeyAvailable, registerPasskey } from "../utils/webauthn";

interface Props {
  onDone: () => void;
  onSkip: () => void;
}

export default function PasskeyPromptModal({ onDone, onSkip }: Props) {
  const { t } = useTranslation();
  const [deviceName, setDeviceName] = useState(guessDeviceName());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);

  if (available === null) {
    void passkeyAvailable().then(setAvailable);
  }

  const register = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await registerPasskey(deviceName.trim() || guessDeviceName());
      onDone();
    } catch (err) {
      setError((err as Error).message || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("auth.passkey.registerTitle")}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {t("auth.passkey.registerBody")}
        </p>

        {available === false && (
          <p className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
            {t("auth.passkey.notAvailable")}
          </p>
        )}

        <label className="mt-4 block text-sm">
          <span className="block font-medium text-gray-700">
            {t("auth.passkey.deviceName")}
          </span>
          <input
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            className="input mt-1"
          />
        </label>

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            {t("auth.passkey.skipForNow")}
          </button>
          <button
            type="button"
            onClick={register}
            disabled={submitting || available === false}
            className="rounded-lg bg-pme-blue px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {submitting ? t("common.loading") : t("auth.passkey.register")}
          </button>
        </div>
      </div>
    </div>
  );
}
